import { useEffect, useState, useCallback } from "react";
import api, { money, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import PLSplitEditor from "@/components/PLSplitEditor";
import { Sparkle } from "@phosphor-icons/react";

const FREQS = ["Monthly", "Quarterly", "Annual", "Weekly", "Custom"];
const TYPES = [
  { v: "PREPAYMENT", label: "Prepayment", codesKey: "prepayment_codes", pl: "EXPENSE" },
  { v: "ACCRUAL", label: "Accrual", codesKey: "accrual_codes", pl: "EXPENSE" },
  { v: "DEFERRED_INCOME", label: "Deferred Income", codesKey: "deferred_income_codes", pl: "REVENUE" },
];
const today = () => new Date().toISOString().slice(0, 10);
const inOneYear = () => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0, 10); };
const computeEndDate = (startStr, freq, periods) => {
  const n = Number(periods);
  if (!startStr || !n || n < 1) return null;
  const d = new Date(startStr + "T00:00:00");
  if (freq === "Weekly") { d.setDate(d.getDate() + n * 7 - 1); }
  else {
    const step = freq === "Annual" ? 12 : freq === "Quarterly" ? 3 : 1;
    d.setMonth(d.getMonth() + n * step);
    d.setDate(d.getDate() - 1);
  }
  return d.toISOString().slice(0, 10);
};

export default function CreateAdjustmentModal({ open, onClose, onCreated, prefill = null }) {
  const [accounts, setAccounts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [trackCats, setTrackCats] = useState([]);
  const [category, setCategory] = useState("PREPAYMENT");
  const [bsCode, setBsCode] = useState("");
  const [plCode, setPlCode] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(today());
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [frequency, setFrequency] = useState("Monthly");
  const [basis, setBasis] = useState("monthly");
  const [rounding, setRounding] = useState("last");
  const [numPeriods, setNumPeriods] = useState(6);
  const [periodsInput, setPeriodsInput] = useState("");
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(inOneYear());
  const [tracking, setTracking] = useState({});
  const [releaseOnly, setReleaseOnly] = useState(true);
  const [plSplit, setPlSplit] = useState({ enabled: false, valid: true, pl_split: [] });
  const [splitSeed, setSplitSeed] = useState(null);
  const [splitKey, setSplitKey] = useState(0);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInfo, setAiInfo] = useState(null);
  const [templates, setTemplates] = useState([]);

  const resetForm = useCallback(() => {
    setCategory("PREPAYMENT");
    setBsCode(""); setPlCode("");
    setAmount(""); setReference(""); setDescription("");
    setDate(today());
    setFrequency("Monthly"); setBasis("monthly"); setRounding("last");
    setNumPeriods(6); setPeriodsInput("");
    setStart(today()); setEnd(inOneYear());
    setTracking({}); setPreview(null); setAiInfo(null);
    setReleaseOnly(true);
    setPlSplit({ enabled: false, valid: true, pl_split: [] });
    setSplitSeed(null);
    setSplitKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!open) return;
    resetForm(); // always open a fresh, blank adjustment form
    api.get("/accounts").then(({ data }) => setAccounts(data.accounts)).catch(() => {});
    api.get("/settings").then(({ data }) => setSettings(data.settings)).catch(() => {});
    api.get("/tracking-categories").then(({ data }) => setTrackCats(data.categories)).catch(() => {});
    api.get("/schedule-templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, [open, resetForm]);

  // Prefill from the sync diagnostic (feature: one-tap "Create release-only schedule").
  useEffect(() => {
    if (!open || !prefill) return;
    if (prefill.category) setCategory(prefill.category);
    if (prefill.amount != null) setAmount(String(prefill.amount));
    if (prefill.date) setDate(String(prefill.date).slice(0, 10));
    if (prefill.reference) setReference(prefill.reference);
    if (prefill.description) setDescription(prefill.description);
    setReleaseOnly(true);
    setTimeout(() => { if (prefill.source_account_code) setBsCode(prefill.source_account_code); }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill]);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    if (t.category) setCategory(t.category);
    const f = FREQS.find((x) => x.toLowerCase() === (t.frequency || "").toLowerCase());
    if (f) setFrequency(f);
    if (t.num_periods) setNumPeriods(t.num_periods);
    if (t.release_basis) setBasis(t.release_basis);
    if (t.description) setDescription(t.description);
    // bs/pl applied after the category-change reset effect settles
    setTimeout(() => {
      if (t.source_account_code) setBsCode(t.source_account_code);
      if (t.pl_account_code) setPlCode(t.pl_account_code);
    }, 0);
    setSplitSeed(t.pl_split && t.pl_split.length >= 2 ? t.pl_split : null);
    setSplitKey((k) => k + 1);
    toast.success(`Applied template "${t.name}"`);
  };

  const typeDef = TYPES.find((t) => t.v === category);
  // Only offer codes that are BOTH marked as this type AND present in this entity's
  // imported Xero chart. This prevents orphaned/legacy codes (e.g. defaults left in an
  // entity's saved classification but not in its chart) from appearing to be picked.
  const bsCandidates = (settings?.[typeDef.codesKey] || [])
    .map((c) => accounts.find((a) => a.code === c))
    .filter(Boolean);
  const plCandidates = accounts.filter((a) => a.class === typeDef.pl);

  // reset code selections when type changes (preserve an already-valid pick, e.g. from prefill)
  useEffect(() => {
    setBsCode((prev) => (prev && bsCandidates.find((a) => a.code === prev)) ? prev : (bsCandidates[0]?.code || ""));
    setPlCode((prev) => (prev && plCandidates.find((a) => a.code === prev)) ? prev : (plCandidates[0]?.code || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, settings, accounts]);

  const loadPreview = useCallback(async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { setPreview(null); return; }
    try {
      const { data } = await api.post("/schedules/preview", {
        original_amount: amt, start_date: start, end_date: end,
        frequency: frequency.toLowerCase(), num_periods: Number(numPeriods), category,
        release_basis: basis, rounding,
      });
      setPreview(data.preview);
    } catch { setPreview(null); }
  }, [amount, start, end, frequency, numPeriods, category, basis, rounding]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(loadPreview, 250);
    return () => clearTimeout(t);
  }, [loadPreview, open]);

  const amt = Number(amount) || 0;
  const round2 = (n) => Math.round(n * 100) / 100;
  const accName = (code) => bsCandidates.find((a) => a.code === code)?.name
    || accounts.find((a) => a.code === code)?.name || code;
  const isPrepay = category === "PREPAYMENT";
  // The set-up journal posts the BS code at the FULL total; the P&L side is shown as the
  // unrounded period total (base × periods) plus a balancing rounding-adjustment line on
  // the same P&L nominal, so the journal still ties back to the total.
  const hasBreakdown = Boolean(!plSplit.enabled && preview && preview.release_basis !== "daily" && preview.periods > 1
    && Math.abs(preview.rounding_adjustment || 0) > 0.0001 && plCode && bsCode && amt);
  const baseTotal = preview ? round2((preview.base_per_period || 0) * (preview.periods || 0)) : 0;
  const residual = preview ? round2(amt - baseTotal) : 0;
  const splitParts = () => {
    const sp = plSplit.pl_split || [];
    let running = 0; const out = [];
    sp.forEach((s, i) => {
      const part = i === sp.length - 1 ? round2(amt - running) : round2(amt * (s.percent / 100));
      running = round2(running + part);
      out.push({ code: s.pl_account_code, name: accName(s.pl_account_code), part });
    });
    return out;
  };
  const setupLines = !amt || !bsCode ? [] :
    (plSplit.enabled && plSplit.valid
      ? (isPrepay
          ? [{ code: bsCode, name: accName(bsCode), debit: amt, credit: 0 },
             ...splitParts().map((p) => ({ code: p.code, name: p.name, debit: 0, credit: p.part }))]
          : [...splitParts().map((p) => ({ code: p.code, name: p.name, debit: p.part, credit: 0 })),
             { code: bsCode, name: accName(bsCode), debit: 0, credit: amt }])
      : (!plCode ? [] : (isPrepay
          ? [{ code: bsCode, name: accName(bsCode), debit: amt, credit: 0 },
             { code: plCode, name: accName(plCode), debit: 0, credit: amt }]
          : [{ code: plCode, name: accName(plCode), debit: amt, credit: 0 },
             { code: bsCode, name: accName(bsCode), debit: 0, credit: amt }])));

  const suggestWithAI = async () => {
    if (!Number(amount) || Number(amount) <= 0) { toast.error("Enter an amount first"); return; }
    setAiLoading(true); setAiInfo(null);
    try {
      const { data } = await api.post("/schedules/ai-suggest", {
        category, amount: Number(amount), reference: reference.trim(), description, date,
      });
      const s = data.suggestion || {};
      if (s.start_date) setStart(s.start_date);
      if (s.end_date) setEnd(s.end_date);
      if (s.frequency) { const f = FREQS.find((x) => x.toLowerCase() === s.frequency.toLowerCase()); if (f) setFrequency(f); }
      if (s.release_basis) setBasis(s.release_basis);
      if (s.num_periods) setNumPeriods(s.num_periods);
      if (s.pl_account_code) setPlCode(s.pl_account_code);
      if (s.description) setDescription(s.description);
      setAiInfo({ reasoning: s.reasoning, plWarning: data.pl_warning });
      toast.success("AI filled the schedule — review before posting");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI suggestion failed");
    } finally { setAiLoading(false); }
  };

  const submit = async () => {
    if (!bsCode) { toast.error("Select a balance-sheet code"); return; }
    if (!plCode) { toast.error("Select a P&L code"); return; }
    if (!Number(amount) || Number(amount) <= 0) { toast.error("Enter an amount"); return; }
    if (!reference.trim()) { toast.error("Enter a reference"); return; }
    if (plSplit.enabled && !plSplit.valid) { toast.error("Fix the P&L split — codes must be unique and total 100%"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/adjustments", {
        category, source_account_code: bsCode,
        pl_account_code: plSplit.enabled && plSplit.pl_split[0] ? plSplit.pl_split[0].pl_account_code : plCode,
        amount: Number(amount), date, reference: reference.trim(), description,
        frequency: frequency.toLowerCase(), num_periods: Number(numPeriods),
        start_date: start, end_date: end, release_basis: basis, rounding,
        release_only: releaseOnly,
        pl_split: plSplit.enabled ? plSplit.pl_split : [],
        tracking: Object.entries(tracking).filter(([, v]) => v).map(([name, option]) => ({ name, option })),
      });
      toast.success(releaseOnly
        ? "Release schedule created · no set-up journal posted (opening balance already in Xero)"
        : `Adjustment created · set-up journal posted to Xero (${data.setup_xero_id || "ok"})`);
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create adjustment");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-lg border-slate-200 p-0 gap-0 max-h-[92vh] overflow-y-auto" data-testid="create-adjustment-dialog">
        <DialogHeader className="px-7 pt-6 pb-4 border-b border-slate-100">
          <div className="overline text-orangeink mb-1">New adjustment</div>
          <DialogTitle className="font-heading font-bold text-2xl tracking-tighter text-navy">
            Create an accrual, prepayment or deferred income
          </DialogTitle>
          <DialogDescription className="text-navy/70">
            {releaseOnly
              ? "The opening balance is already in Xero — WWL will only schedule the release/reversal over time (no set-up journal posted)."
              : "Posts the set-up journal to Xero now and schedules the release/reversal over time."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-7 py-6">
          <div className="md:col-span-2">
            <Lbl>Type</Lbl>
            <div className="flex border border-slate-200 rounded-lg overflow-hidden text-sm w-fit">
              {TYPES.map((t) => (
                <button key={t.v} data-testid={`adj-type-${t.v}`} onClick={() => setCategory(t.v)}
                  className={`px-4 py-2 font-medium transition-colors border-l border-slate-100 first:border-l-0 ${
                    category === t.v ? "bg-orange text-white" : "bg-white text-navy/70 hover:bg-navy/5"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <Lbl>Set-up journal</Lbl>
            <div className="flex border border-slate-200 rounded-lg overflow-hidden text-sm w-fit" data-testid="adj-mode-toggle">
              <button type="button" data-testid="adj-mode-release-only" onClick={() => setReleaseOnly(true)}
                className={`px-4 py-2 font-medium transition-colors ${
                  releaseOnly ? "bg-orange text-white" : "bg-white text-navy/70 hover:bg-navy/5"}`}>
                Release only (already in Xero)
              </button>
              <button type="button" data-testid="adj-mode-post-setup" onClick={() => setReleaseOnly(false)}
                className={`px-4 py-2 font-medium transition-colors border-l border-slate-100 ${
                  !releaseOnly ? "bg-orange text-white" : "bg-white text-navy/70 hover:bg-navy/5"}`}>
                Post a set-up journal
              </button>
            </div>
            {releaseOnly ? (
              <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3.5 py-2.5" data-testid="adj-release-only-warning">
                <span className="text-amber-700 font-bold leading-5">⚠</span>
                <p className="text-xs text-navy/75 leading-relaxed">
                  <span className="font-semibold text-navy">No set-up journal will be posted to Xero.</span> Use this
                  when the opening balance is already in Xero (e.g. posted to the {typeDef.label.toLowerCase()} nominal)
                  but wasn't pulled in by the sync. WWL will only schedule the release/reversal journals from here.
                </p>
              </div>
            ) : (
              <p className="text-xs text-navy/65 mt-1.5">
                WWL will post a set-up journal to Xero now to establish the balance-sheet balance, then schedule the releases.
              </p>
            )}
          </div>

          <div className="md:col-span-2 flex items-center justify-between gap-4 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3">
            <div className="text-sm text-navy/70">
              <span className="font-semibold text-navy">Let AI draft the release.</span> Enter the amount, then let AI suggest the period, frequency and P&amp;L code from your description.
            </div>
            <button onClick={suggestWithAI} disabled={aiLoading} data-testid="adj-ai-suggest-btn"
              className="shrink-0 flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-[#F37A24] text-white hover:bg-navy transition-colors duration-200 disabled:opacity-60">
              <Sparkle size={16} weight="fill" />{aiLoading ? "Thinking…" : "Suggest with AI"}
            </button>
          </div>

          {aiInfo && (
            <div className="md:col-span-2 bg-navy/[0.03] border border-slate-200 rounded-lg p-4 space-y-2" data-testid="adj-ai-info">
              {aiInfo.reasoning && (
                <div className="text-sm text-navy/75"><span className="overline text-navy/65 block mb-0.5">Why</span>{aiInfo.reasoning}</div>
              )}
              {aiInfo.plWarning && (
                <div className="text-xs font-medium text-[#B45309] bg-orange-50 border border-orange-200 rounded-md px-2.5 py-1.5" data-testid="adj-ai-pl-warning">
                  ⚠ {aiInfo.plWarning}
                </div>
              )}
            </div>
          )}

          {templates.filter((t) => t.category === category).length > 0 && (
            <div className="md:col-span-2 flex items-center gap-2" data-testid="adj-template-bar">
              <span className="overline text-navy/70 shrink-0">Template</span>
              <Select value="" onValueChange={applyTemplate}>
                <SelectTrigger data-testid="adj-template-select" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="Apply a saved template…" /></SelectTrigger>
                <SelectContent>
                  {templates.filter((t) => t.category === category).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name} · {t.frequency}{t.num_periods ? ` ×${t.num_periods}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Lbl>Balance-sheet code</Lbl>
            <Select value={bsCode} onValueChange={setBsCode}>
              <SelectTrigger data-testid="adj-bs-code" className="rounded-lg border-slate-200"><SelectValue placeholder="Select code" /></SelectTrigger>
              <SelectContent>
                {bsCandidates.length === 0 && <div className="px-3 py-2 text-xs text-navy/70">Configure codes in Settings → Account Codes.</div>}
                {bsCandidates.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Lbl>Counter P&amp;L code</Lbl>
            <Select value={plCode} onValueChange={setPlCode}>
              <SelectTrigger data-testid="adj-pl-code" className="rounded-lg border-slate-200"><SelectValue placeholder="Select code" /></SelectTrigger>
              <SelectContent>
                {plCandidates.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {plSplit.enabled && <p className="text-xs text-navy/55 mt-1">Used as the primary code; the split below controls the release proportions.</p>}
          </div>

          <PLSplitEditor key={splitKey} plAccounts={plCandidates} total={Number(amount) || 0}
            primaryCode={plCode} initialSplit={splitSeed} onChange={setPlSplit} />

          <div>
            <Lbl>Amount</Lbl>
            <input type="number" min="0" step="0.01" value={amount} data-testid="adj-amount"
              onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
          </div>
          <div>
            <Lbl>{releaseOnly ? "Opening date" : "Date (set-up journal)"}</Lbl>
            <input type="date" value={date} data-testid="adj-date" onChange={(e) => setDate(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
          </div>

          <div>
            <Lbl>Reference</Lbl>
            <input value={reference} data-testid="adj-reference" onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. ACCR-AUDIT-26"
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
          </div>
          <div>
            <Lbl>Release frequency</Lbl>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger data-testid="adj-frequency" className="rounded-lg border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>{FREQS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Lbl>Start date</Lbl>
            <input type="date" value={start} data-testid="adj-start" onChange={(e) => setStart(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
          </div>
          {frequency === "Custom" ? (
            <div>
              <Lbl>Number of periods</Lbl>
              <input type="number" min={1} value={numPeriods} data-testid="adj-num-periods"
                onChange={(e) => setNumPeriods(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
            </div>
          ) : (
            <>
              <div>
                <Lbl>Number of periods</Lbl>
                <input type="number" min={1} value={periodsInput} data-testid="adj-periods-input"
                  onChange={(e) => {
                    setPeriodsInput(e.target.value);
                    const ed = computeEndDate(start, frequency, e.target.value);
                    if (ed) setEnd(ed);
                  }}
                  placeholder="auto-fills end date"
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
              </div>
              <div>
                <Lbl>End date {periodsInput ? "(auto)" : ""}</Lbl>
                <input type="date" value={end} data-testid="adj-end" onChange={(e) => { setEnd(e.target.value); setPeriodsInput(""); }}
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
              </div>
            </>
          )}

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
            <div>
              <Lbl>Release basis</Lbl>
              <div className="flex border border-slate-200 rounded-lg overflow-hidden text-sm w-fit">
                {[{ v: "monthly", l: "Even periods" }, { v: "daily", l: "Daily pro-rata" }].map((b) => (
                  <button key={b.v} type="button" data-testid={`adj-basis-${b.v}`} onClick={() => setBasis(b.v)}
                    className={`px-4 py-2 font-medium transition-colors border-l border-slate-100 first:border-l-0 ${
                      basis === b.v ? "bg-orange text-white" : "bg-white text-navy/70 hover:bg-navy/5"}`}>
                    {b.l}
                  </button>
                ))}
              </div>
              <p className="text-xs text-navy/65 mt-1.5">
                {basis === "daily"
                  ? "Each release is sized by the actual number of days it covers (accurate for part-months)."
                  : "The total is split evenly across every release period."}
              </p>
            </div>

            {basis !== "daily" && (
              <div>
                <Lbl>Rounding adjustment on</Lbl>
                <div className="flex border border-slate-200 rounded-lg overflow-hidden text-sm w-fit">
                  {[{ v: "last", l: "Last period" }, { v: "first", l: "First period" }].map((b) => (
                    <button key={b.v} type="button" data-testid={`adj-rounding-${b.v}`} onClick={() => setRounding(b.v)}
                      className={`px-4 py-2 font-medium transition-colors border-l border-slate-100 first:border-l-0 ${
                        rounding === b.v ? "bg-orange text-white" : "bg-white text-navy/70 hover:bg-navy/5"}`}>
                      {b.l}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-navy/65 mt-1.5">Rounding difference lands here so periods sum exactly to the total.</p>
              </div>
            )}
          </div>

          {trackCats.length > 0 && (
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-5">
              {trackCats.map((tc) => (
                <div key={tc.name}>
                  <Lbl>Tracking · {tc.name}</Lbl>
                  <Select value={tracking[tc.name] || ""} onValueChange={(v) => setTracking({ ...tracking, [tc.name]: v })}>
                    <SelectTrigger data-testid={`adj-tracking-${tc.name}`} className="rounded-lg border-slate-200"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>{tc.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}

          <div className="md:col-span-2">
            <Lbl>Description</Lbl>
            <Textarea data-testid="adj-description" value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} className="rounded-lg border-slate-200 focus:border-orange" placeholder="Journal narration" />
          </div>
        </div>

        {!releaseOnly && setupLines.length > 0 && (
          <div className="px-7 pb-2">
            <div className="overline text-navy/65 mb-2">Set-up journal (posts to Xero on {fmtDate(date)})</div>
            <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="adj-journal-review">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-navy/[0.04] overline text-navy/65">
                    <th className="text-left px-4 py-2 font-semibold">Account</th>
                    <th className="text-right px-4 py-2 font-semibold">Debit</th>
                    <th className="text-right px-4 py-2 font-semibold">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {!hasBreakdown && setupLines.map((l, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-4 py-2"><span className="font-mono font-medium text-navy">{l.code}</span> <span className="text-navy/70">{l.name}</span></td>
                      <td className="px-4 py-2 text-right font-mono tabular text-navy">{l.debit ? money(l.debit) : "—"}</td>
                      <td className="px-4 py-2 text-right font-mono tabular text-navy">{l.credit ? money(l.credit) : "—"}</td>
                    </tr>
                  ))}
                  {hasBreakdown && (
                    <>
                      {isPrepay ? (
                        <>
                          <JRow code={bsCode} name={accName(bsCode)} debit={amt} />
                          <JRow code={plCode} name={accName(plCode)} credit={baseTotal} sub={`${preview.periods} × ${money(preview.base_per_period)}`} />
                          <AdjRow code={plCode} name={accName(plCode)} credit={residual} rounding={rounding} setRounding={setRounding} period={preview.rounding_period} />
                        </>
                      ) : (
                        <>
                          <JRow code={plCode} name={accName(plCode)} debit={baseTotal} sub={`${preview.periods} × ${money(preview.base_per_period)}`} />
                          <AdjRow code={plCode} name={accName(plCode)} debit={residual} rounding={rounding} setRounding={setRounding} period={preview.rounding_period} />
                          <JRow code={bsCode} name={accName(bsCode)} credit={amt} />
                        </>
                      )}
                    </>
                  )}
                  <tr className="border-t border-slate-200 bg-navy/[0.03] font-semibold">
                    <td className="px-4 py-2 text-navy">Total</td>
                    <td className="px-4 py-2 text-right font-mono tabular text-navy">{money(amt)}</td>
                    <td className="px-4 py-2 text-right font-mono tabular text-navy">{money(amt)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {preview && (
          <div className="px-7 pb-5">
            <div className="bg-navy/[0.03] border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="overline text-navy/65">Calculated release schedule</div>
                <div className="text-sm font-mono text-navy/70" data-testid="adj-preview">
                  {preview.periods} periods · {preview.release_basis === "daily" ? "daily pro-rata" : `${money(preview.per_period_amount)} / period`} · ends {fmtDate(preview.final_date)}
                </div>
              </div>
              {preview.rounding_adjustment !== 0 && preview.release_basis !== "daily" && (
                <div data-testid="adj-rounding-note" className="text-xs text-navy/70 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-3 space-y-0.5">
                  <div>{preview.periods} × {money(preview.base_per_period)} = <span className="font-semibold">{money(preview.base_per_period * preview.periods)}</span> vs total {money(Number(amount) || 0)}</div>
                  <div>
                    Variance <span className="font-semibold text-orangeink">{preview.rounding_adjustment > 0 ? "+" : ""}{money(preview.rounding_adjustment)}</span> posted to the{" "}
                    <span className="font-semibold">{rounding === "first" ? "first" : "last"}</span> period (period {preview.rounding_period} = {money(preview.base_per_period + preview.rounding_adjustment)}). Use the toggle above to move it.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 px-7 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <button onClick={onClose} data-testid="adj-cancel-btn"
            className="px-5 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="adj-submit-btn"
            className="px-6 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors duration-200 disabled:opacity-60">
            {saving ? "SAVING…" : (releaseOnly ? "Create release schedule" : "Create & post to Xero")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Lbl({ children }) { return <span className="overline text-navy/70 block mb-1.5">{children}</span>; }

function JRow({ code, name, debit, credit, sub }) {
  return (
    <tr className="border-t border-slate-100">
      <td className="px-4 py-2">
        <span className="font-mono font-medium text-navy">{code}</span> <span className="text-navy/70">{name}</span>
        {sub && <span className="text-navy/65 text-xs"> · {sub}</span>}
      </td>
      <td className="px-4 py-2 text-right font-mono tabular text-navy">{debit ? money(debit) : "—"}</td>
      <td className="px-4 py-2 text-right font-mono tabular text-navy">{credit ? money(credit) : "—"}</td>
    </tr>
  );
}

function AdjRow({ code, name, debit, credit, rounding, setRounding, period }) {
  const amt = debit || credit;
  return (
    <tr className="border-t border-slate-100 bg-amber-50/60" data-testid="adj-journal-rounding-line">
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[0.6rem] uppercase tracking-wider font-semibold text-amber-700">Rounding adj.</span>
          <span className="font-mono font-medium text-navy">{code}</span>
          <span className="text-navy/70 text-xs">{name}</span>
          <span className="text-navy/65 text-xs">· on</span>
          <Select value={rounding} onValueChange={setRounding}>
            <SelectTrigger data-testid="adj-rounding-period-select" className="h-7 w-[124px] rounded-md border-amber-300 bg-white text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="first">First period</SelectItem>
              <SelectItem value="last">Last period</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-navy/65 text-xs">(period {period})</span>
        </div>
      </td>
      <td className="px-4 py-2.5 text-right font-mono tabular text-amber-800">{debit ? money(amt) : "—"}</td>
      <td className="px-4 py-2.5 text-right font-mono tabular text-amber-800">{credit ? money(amt) : "—"}</td>
    </tr>
  );
}
